package com.banyan.flightviewer.web.platform

import com.banyan.flightviewer.state.AppState
import com.banyan.flightviewer.state.AtcStreamInfo
import com.banyan.flightviewer.state.AtcStreamStatus
import kotlinx.browser.document
import org.w3c.dom.HTMLAudioElement

/**
 * Wraps an HTMLAudioElement and plays a LiveATC mount through the
 * `:server` proxy so we get CORS headers and can attach Web Audio later.
 *
 * Mirrors the desktop `AtcAudioManager` state transitions on
 * `AppState.atcStream` / `atcStatus` so the player bar UI is identical.
 */
class WebAudioPlayer(private val appState: AppState) {

    private val audio: HTMLAudioElement = (document.createElement("audio") as HTMLAudioElement).also {
        it.preload = "none"
        // No crossOrigin attribute: the server redirects us straight to
        // LiveATC, and they don't set CORS headers. Audio playback doesn't
        // need CORS unless we want to attach Web Audio API nodes.
        it.volume = appState.atcVolume.value
        it.addEventListener("loadeddata", { appState.setAtcStatus(AtcStreamStatus.PLAYING) })
        it.addEventListener("playing", { appState.setAtcStatus(AtcStreamStatus.PLAYING) })
        it.addEventListener("waiting", { appState.setAtcStatus(AtcStreamStatus.STALLED) })
        it.addEventListener("error", {
            appState.setAtcStatus(AtcStreamStatus.ERROR)
            appState.setStatusMessage("ATC audio error")
        })
        it.addEventListener("ended", {
            appState.setAtcStatus(null)
            appState.setAtcStream(null)
        })
    }

    fun play(stream: AtcStreamInfo) {
        // Stop any current playback first.
        audio.pause()
        audio.removeAttribute("src")
        audio.load()

        appState.setAtcStream(stream)
        appState.setAtcStatus(AtcStreamStatus.LOADING)

        // Extract the LiveATC mount name from the stream URL and route through
        // the server proxy. Both desktop and web feed URLs follow the
        // pattern "https://d.liveatc.net/<mount>".
        val mount = stream.url.substringAfterLast("/")
        audio.src = "/api/atc/stream?mount=$mount"
        val playPromise = audio.play()
        if (playPromise != null) {
            playPromise.asDynamic().catch { err: dynamic ->
                appState.setAtcStatus(AtcStreamStatus.ERROR)
                appState.setStatusMessage("ATC audio: ${err.message}")
                Unit
            }
        }
    }

    fun stop() {
        audio.pause()
        audio.removeAttribute("src")
        audio.load()
        appState.setAtcStream(null)
        appState.setAtcStatus(null)
    }

    fun setVolume(v: Double) {
        val clamped = v.coerceIn(0.0, 1.0)
        appState.setAtcVolume(clamped)
        audio.volume = clamped
    }
}
